import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { reportsKeys } from "Reports/SavedReports/API/keys";

import {
  addColumn,
  archiveBoard,
  deleteBoard,
  getAllAvailableBoards,
  getSacramentStructures,
  linkColumnToBoard,
  saveDefaultFilter,
} from "./fetch";
import { boardsKeys } from "./keys";

export const useSaveDefaultFilters = ({ boardID, onSuccess, onError }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: boardsKeys.saveDefaultFilter({ boardID }),
    mutationFn: ({ boardID, groupID, filters }) => {
      return saveDefaultFilter({
        boardID,
        groupID: groupID ? groupID : null,
        filters: filters ? filters : null,
      });
    },
    onSuccess: (resp) => {
      onSuccess && onSuccess(resp);
      queryClient.invalidateQueries(reportsKeys.getReportBoard({ boardID }));
    },
    onError: (err) => {
      onError && onError(err);
    },
  });
};

export const useArchiveBoard = ({ boardID, onSuccess }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: boardsKeys.archiveBoard({ boardID }),
    mutationFn: ({ archive }) => archiveBoard({ boardID, archive }),
    onSuccess: (resp) => {
      onSuccess && onSuccess(resp);
      queryClient.invalidateQueries(reportsKeys.all());
    },
  });
};

export const useDeleteBoard = ({ boardID, onSuccess }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: boardsKeys.deleteBoard({ boardID }),
    mutationFn: ({ groupURL }) => deleteBoard({ boardID, groupURL }),
    onSuccess: (resp) => {
      onSuccess && onSuccess(resp);
      queryClient.invalidateQueries(reportsKeys.getReportsList());
    },
  });
};

export const useGetAvailableBoards = ({
  enabled = true,
  columnID,
  currentBoardID,
}) => {
  return useQuery({
    queryKey: boardsKeys.getAllAvailableBoards(),
    queryFn: () => getAllAvailableBoards({ columnID, currentBoardID }),
    enabled,
  });
};

export const useLinkColumnToBoard = ({
  columnID,
  groupID,
  boardID,
  onSuccess,
}) => {
  return useMutation({
    mutationKey: boardsKeys.linkColumnToBoard({ columnID, boardID }),
    mutationFn: () =>
      linkColumnToBoard({ boardID, groupID, linkColumnID: columnID }),
    onSuccess: (resp) => {
      onSuccess && onSuccess(resp);
    },
  });
};

export const usePrefetchGetAvailableBoards = ({ columnID, currentBoardID }) => {
  const queryClient = useQueryClient();
  const prefetch = () => {
    queryClient.prefetchQuery({
      queryKey: boardsKeys.getAllAvailableBoards(),
      queryFn: () => getAllAvailableBoards({ columnID, currentBoardID }),
      staleTime: 10000,
    });
  };

  return { prefetch };
};

export const useGetSacramentStructures = ({
  boardID,
  enabled = true,
  onSuccess,
}) => {
  return useQuery({
    queryKey: boardsKeys.getSacramentStructures(),
    queryFn: () => getSacramentStructures({ boardID }),
    onSuccess: (resp) => {
      onSuccess && onSuccess(resp);
    },
    enabled: enabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const usePrefetchSacramentStructures = ({ boardID }) => {
  const queryClient = useQueryClient();

  const prefetch = () => {
    queryClient.prefetchQuery({
      queryKey: boardsKeys.getSacramentStructures(),
      queryFn: () => getSacramentStructures({ boardID }),
      staleTime: 2000,
    });
  };

  return { prefetch };
};

export const useAddColumn = ({ boardID, networkFieldID, onSuccess }) => {
  // const queryClient = useQueryClient();
  return useMutation({
    mutationKey: boardsKeys.addColumn({ boardID, networkFieldID }),
    mutationFn: ({ postData }) => addColumn({ boardID, postData }),
    onSuccess: (resp) => {
      onSuccess && onSuccess(resp);
    },
  });
};
